// Kasa Tanıtım Kartları — paylaşılan UI bileşenleriyle yeniden tasarlandı.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import AuditPopover from '../components/AuditPopover';
import DataTable from '../components/DataTable';
import { TabIcon, TAB_ICONS } from '../utils/tabIcons';
import { Button, Modal, FormField, Pager, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero, CardStatusBadge, CardTabs, CardSplitButton } from '../components/card';

const CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP', 'CHF', 'SAR', 'AED'];
const emptyForm = { register_name: '', currency_code: 'TRY', notes: '', is_active: true, branch_id: '' };

export default function CashRegistersPage() {
  const [registers, setRegisters] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 50;
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ currency_code: '', is_active: '' });

  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [isEditing, setIsEditing] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchRegisters = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/cash-registers', { params });
      if (res.data.success) setRegisters(res.data.data.items || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, filters]);
  useEffect(() => { fetchRegisters(); }, [fetchRegisters]);
  useEffect(() => {
    api.get('/branches', { params: { is_active: 'yes' } })
      .then(r => { if (r.data?.success) setBranches(r.data.data.items || []); })
      .catch(() => {});
  }, []);

  const openNew = () => {
    setSelected(null); setForm({ ...emptyForm });
    setIsNew(true); setIsEditing(true); setActiveTab('general'); setMsg(null);
    setShowModal(true);
  };
  const openDetail = async (reg) => {
    try {
      const res = await api.get('/cash-registers/' + reg.id);
      if (res.data.success) {
        const d = res.data.data;
        setSelected(d); setForm({ ...d, is_active: d.is_active === true || d.is_active === 1 });
        setIsEditing(false); setIsNew(false); setActiveTab('general'); setMsg(null);
        setShowModal(true);
      }
    } catch {}
  };
  const closeModal = () => { setShowModal(false); setSelected(null); setIsEditing(false); setIsNew(false); setMsg(null); };

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    if (!form.register_name?.trim()) { setMsg({ type: 'error', text: 'Kasa adı zorunludur.' }); return; }
    setSaving(true); setMsg(null);
    try {
      if (isNew) {
        const res = await api.post('/cash-registers', form);
        setIsNew(false); setIsEditing(false);
        await fetchRegisters();
        const r2 = await api.get('/cash-registers/' + res.data.data.id);
        if (r2.data.success) { setSelected(r2.data.data); setForm({ ...r2.data.data, is_active: r2.data.data.is_active === true || r2.data.data.is_active === 1 }); }
        setMsg({ type: 'success', text: 'Kasa oluşturuldu.' });
      } else {
        await api.put('/cash-registers/' + selected.id, form);
        setIsEditing(false);
        await fetchRegisters();
        const r2 = await api.get('/cash-registers/' + selected.id);
        if (r2.data.success) { setSelected(r2.data.data); setForm({ ...r2.data.data, is_active: r2.data.data.is_active === true || r2.data.data.is_active === 1 }); }
        setMsg({ type: 'success', text: 'Kaydedildi.' });
      }
      if (mode === 'close') setTimeout(() => closeModal(), 250);
      else if (mode === 'new') setTimeout(() => { setSelected(null); setForm({ register_name: '', currency_code: 'TRY', is_active: true }); setIsEditing(true); setIsNew(true); setMsg(null); }, 250);
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi.' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`"${selected.register_name}" kasasını silmek istiyor musunuz?`)) return;
    try { await api.delete('/cash-registers/' + selected.id); closeModal(); fetchRegisters(); }
    catch (err) { setMsg({ type: 'error', text: err.response?.data?.message || 'Silinemedi.' }); }
  };

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const filtered = registers.filter(r => !search || r.register_name?.toLowerCase().includes(search.toLowerCase()) || r.currency_code?.toLowerCase().includes(search.toLowerCase()));
  const totalPages = Math.ceil(filtered.length / LIMIT) || 1;
  const paged = filtered.slice((page - 1) * LIMIT, page * LIMIT);

  // Modal aksiyonları — footer'a taşındı (yeni Modal API)
  const inEdit = isEditing;
  const inView = !isEditing && !isNew;
  const handleCancel = () => {
    if (isNew) closeModal();
    else { setIsEditing(false); setForm({ ...selected, is_active: selected.is_active === true || selected.is_active === 1 }); setMsg(null); }
  };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Kasa Tanıtım Kartları"
        count={registers.length}
        unit="kasa"
        search={search}
        onSearch={v => { setSearch(v); setPage(1); }}
        searchPlaceholder="Kasa ara..."
        onRefresh={fetchRegisters}
        refreshing={loading}
        actions={<Button size="sm" onClick={openNew}>+ Yeni Kasa</Button>}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'currency_code', type: 'select', label: 'Para Birimi',
            options: [{ value: '', label: 'Tümü' }, ...CURRENCIES.map(c => ({ value: c, label: c }))] },
          { key: 'is_active', type: 'select', label: 'Durum',
            options: [{ value: '', label: 'Tümü' }, { value: 'yes', label: 'Aktif' }, { value: 'no', label: 'Pasif' }] },
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="cash-registers-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Kasa bulunamadı"
          data={paged}
          rowKey="id"
          onRowClick={reg => openDetail(reg)}
          columns={[
            { id: 'register_name', header: 'Kasa Adı', accessor: 'register_name', sortable: true,
              cell: reg => <span style={{ fontWeight: 600 }}>{reg.register_name}</span> },
            { id: 'currency_code', header: 'Para Birimi', accessor: 'currency_code', sortable: true, width: 110,
              cell: reg => <span className="stock-secondary">{reg.currency_code}</span> },
            { id: 'status', header: 'Durum', accessor: r => r.is_active ? 'Aktif' : 'Pasif', sortable: true, width: 100,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: reg => <span className={'stock-badge ' + (reg.is_active ? 'active' : 'passive')}>{reg.is_active ? 'Aktif' : 'Pasif'}</span> },
            { id: 'audit', header: '', sortable: false, hideable: false, draggable: false, filterable: false, width: 32,
              cellStyle: { textAlign: 'center' },
              cell: reg => <span onClick={e => e.stopPropagation()}><AuditPopover endpoint={`/cash-registers/${reg.id}/audit`} /></span> },
          ]}
        />
      </div>

      <Pager page={page} totalPages={totalPages} onChange={setPage} />

      {showModal && (
        <Modal
          icon={null}
          title={isNew ? 'Yeni Kasa' : 'Kasa Kartı'}
          size="md"
          onClose={closeModal}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {inView && <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {inEdit && <Button variant="ghost" onClick={handleCancel}>{isNew ? 'Vazgeç' : 'İptal'}</Button>}
                {inEdit && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => handleSave('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: () => handleSave('new') },
                      { label: 'Kaydet ve Kapat',  icon: '✓', onClick: () => handleSave('close') },
                    ]}
                  />
                )}
                {inView && <Button onClick={() => { setIsEditing(true); setMsg(null); }}>✎ Düzenle</Button>}
              </div>
            </>
          }
        >
          {!isNew && (
            <CardHero
              icon="🏪"
              code={form.register_name}
              title={form.register_name || 'Kasa'}
              subtitle={form.currency_code || 'TRY'}
              statusBadge={<CardStatusBadge preset={form.is_active === false ? 'inactive' : 'active'} />}
              kpis={[
                selected?.current_balance != null && {
                  label: 'Bakiye',
                  value: parseFloat(selected.current_balance).toLocaleString('tr-TR'),
                  color: selected.current_balance >= 0 ? 'success' : 'danger',
                  sublabel: form.currency_code || 'TRY',
                },
              ].filter(Boolean)}
            />
          )}
          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          <CardTabs
            items={[
              { key: 'general', label: 'Genel Bilgiler', icon: <TabIcon name="general"/> },
              { key: 'notes',   label: 'Notlar',         icon: <TabIcon name="notes"/> },
            ]}
            active={activeTab}
            onChange={setActiveTab}
          />

          {activeTab === 'general' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <FormField label="Kasa Adı" required fullWidth>
                <input value={form.register_name || ''} onChange={e => f('register_name', e.target.value)} disabled={!isEditing} placeholder="Örnek: Ana Kasa, Dolar Kasa..." />
              </FormField>
              <FormField label="Para Birimi">
                <select value={form.currency_code || 'TRY'} onChange={e => f('currency_code', e.target.value)} disabled={!isEditing}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </FormField>
              <FormField label="Bağlı Şube">
                <select value={form.branch_id || ''} onChange={e => f('branch_id', e.target.value || null)} disabled={!isEditing}>
                  <option value="">— Seçiniz —</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.code ? b.code + ' - ' : ''}{b.name}</option>)}
                </select>
              </FormField>
              <FormField label="Durum">
                <select value={form.is_active ? '1' : '0'} onChange={e => f('is_active', e.target.value === '1')} disabled={!isEditing}>
                  <option value="1">Aktif</option>
                  <option value="0">Pasif</option>
                </select>
              </FormField>
            </div>
          )}

          {activeTab === 'notes' && (
            <FormField label="Notlar" fullWidth>
              <textarea rows="6" value={form.notes || ''} onChange={e => f('notes', e.target.value)} disabled={!isEditing} placeholder="Bu kasayla ilgili notlar..." />
            </FormField>
          )}
        </Modal>
      )}
    </div>
  );
}
